<template>
    <div class="fixed bottom-6 right-6 z-[9999]">
        <!-- Floating trigger -->
        <button
            class="w-12 h-12 rounded-full bg-indigo-500 border-[3px] border-white shadow-lg shadow-indigo-300/50 text-white font-bold text-sm tracking-wide transition-transform hover:bg-indigo-600 hover:scale-105 focus:outline-none"
            :title="`${$t('global.impersonate.signed_in_as')} ${fullName}`"
            @click="toggleDropdown"
        >
            {{ initials }}
        </button>

        <!-- Dropdown -->
        <div
            v-if="dropdownOpen"
            class="absolute bottom-14 right-0 w-72 bg-white rounded-xl shadow-2xl overflow-hidden"
        >
            <!-- Header -->
            <div class="px-4 pt-4 pb-3 border-b border-gray-100">
                <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">{{ $t('global.impersonate.signed_in_as') }}</p>
                <p class="text-[15px] font-semibold text-gray-900 mt-0.5">{{ fullName }}</p>
            </div>

            <!-- Search -->
            <div class="px-3 py-2.5 border-b border-gray-100">
                <input
                    ref="searchInput"
                    v-model="searchQuery"
                    type="text"
                    :placeholder="$t('global.impersonate.search_placeholder')"
                    class="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm outline-none focus:border-indigo-400 transition-colors"
                    @input="onSearch"
                />
            </div>

            <!-- User list -->
            <ul class="max-h-56 overflow-y-auto">
                <li
                    v-for="user in users"
                    :key="user.id"
                    class="px-4 py-2.5 flex flex-col cursor-pointer border-b border-gray-50 hover:bg-indigo-50 transition-colors"
                    :class="{ 'bg-indigo-100': user.id === impersonatedUser?.id }"
                    @click="selectUser(user)"
                >
                    <span class="text-[13px] font-medium text-gray-900">{{ user.first_name }} {{ user.last_name }}</span>
                    <span class="text-[11px] text-gray-400">{{ user.user_name }}</span>
                </li>
                <li v-if="users.length === 0 && !loading" class="px-4 py-4 text-center text-sm text-gray-400">
                    {{ $t('global.impersonate.no_users_found') }}
                </li>
            </ul>

            <!-- Footer -->
            <div class="px-3 py-2.5 border-t border-gray-100">
                <button
                    class="w-full py-1.5 border border-red-200 text-red-500 text-sm rounded-md hover:bg-red-50 transition-colors"
                    @click="stopImpersonation"
                >
                    {{ $t('global.impersonate.quit') }}
                </button>
            </div>
        </div>

        <!-- Switch overlay -->
        <transition
            enter-active-class="transition-opacity duration-300"
            leave-active-class="transition-opacity duration-300"
            enter-from-class="opacity-0"
            leave-to-class="opacity-0"
        >
            <div
                v-if="showOverlay"
                class="fixed inset-0 bg-indigo-500/90 flex items-center justify-center z-[99999] pointer-events-none"
            >
                <div class="text-center text-white">
                    <p class="text-sm font-light tracking-[0.3em] uppercase opacity-75">{{ $t('global.impersonate.switching_as') }}</p>
                    <p class="text-4xl font-bold tracking-widest uppercase mt-2">{{ overlayName }}</p>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
    name: 'ImpersonationButton',

    data() {
        return {
            dropdownOpen: false,
            searchQuery: '',
            searchTimeout: null,
            showOverlay: false,
            overlayName: '',
        }
    },

    computed: {
        ...mapGetters({
            impersonatedUser: 'impersonation/impersonatedUser',
            users:            'impersonation/users',
            loading:          'impersonation/loading',
        }),

        fullName() {
            return this.impersonatedUser?.full_name ?? ''
        },

        initials() {
            return this.impersonatedUser?.initials ?? '?'
        },
    },

    methods: {
        ...mapActions({
            switchUser:        'impersonation/switchUser',
            stopImpersonation: 'impersonation/stop',
            fetchUsers:        'impersonation/fetchUsers',
        }),

        async toggleDropdown() {
            this.dropdownOpen = !this.dropdownOpen
            if (this.dropdownOpen) {
                await this.fetchUsers('')
                this.$nextTick(() => this.$refs.searchInput?.focus())
            }
        },

        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => this.fetchUsers(this.searchQuery), 200)
        },

        async selectUser(user) {
            if (user.id === this.impersonatedUser?.id) return

            this.dropdownOpen = false
            this.overlayName  = user.full_name ?? `${user.first_name} ${user.last_name}`
            this.showOverlay  = true

            try {
                await this.switchUser(user.id)
            } finally {
                setTimeout(() => { this.showOverlay = false }, 1500)
            }
        },
    },
}
</script>